/**
 * SoftphoneChip (DOCK-TIDY-01, Seth 2026-10-01: "the browser phone ready
 * button can kind of get in the way of other content") — the phone's on/off
 * switch lives in the HEADER now, with the bell and the user menu, where
 * chrome belongs. It never floats over page content again. One button:
 *
 *   gray headset    = phone off — click to turn on (first click enrolls)
 *   pulsing         = connecting
 *   green dot       = ready; this tab rings for Sedgwick calls — click = off
 *   amber           = problem — click retries
 *
 * The dock keeps the LOUD parts only (ringing card, in-call console).
 */
import { Headphones, Loader2 } from "lucide-react";
import { cn } from "@/lib/cn";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { softphone, useSoftphone } from "@/features/calls/softphone";

export function SoftphoneChip() {
  const allowed = useCan("calls.softphone");
  const soft = useSoftphone(allowed);
  if (!allowed || soft.enrolled === null) return null;

  const ready = soft.status === "ready";
  const starting = soft.status === "starting";
  const error = soft.status === "error";

  return (
    <button
      type="button"
      onClick={() => {
        if (starting) return;
        if (ready) void softphone.turnOff();
        else void softphone.turnOn();
      }}
      title={
        ready
          ? "Browser phone is ON — this tab rings for Sedgwick calls (desk phones too). Click to turn off."
          : starting
            ? "Connecting your browser phone…"
            : error
              ? `Browser phone problem${soft.error ? `: ${soft.error}` : ""} — click to retry`
              : "Turn on the browser phone — answer Sedgwick calls right here (rings alongside the desk phones)"
      }
      aria-label={ready ? "Turn browser phone off" : "Turn browser phone on"}
      className={cn(
        "relative flex h-9 w-9 items-center justify-center rounded-lg transition-colors",
        ready
          ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
          : error
            ? "bg-amber-50 text-amber-700 hover:bg-amber-100"
            : "text-slate-500 hover:bg-slate-100 hover:text-slate-700",
      )}
    >
      {starting ? (
        <Loader2 className="h-[18px] w-[18px] animate-spin" />
      ) : (
        <Headphones className="h-[18px] w-[18px]" />
      )}
      {/* The status dot — color is never the only signal (title + bg too). */}
      {ready && (
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-emerald-500 ring-2 ring-white" />
      )}
      {error && (
        <span className="absolute right-1.5 top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-white" />
      )}
    </button>
  );
}
